import type { SessionId } from '@/types/Session.ts';
import { useSourceStore } from '@/stores/SourceStore.ts';
import { createTextToast } from '@/utils/ToastUtils.ts';
import { requestScreenshotAsync } from '@/wrappers/RequestScreenshotAsync.ts';
import { constrainScreenshotSize, cropImage } from '@/utils/ImageModUtils.ts';
import { logger } from '@/lib/logger.ts';

export const captureScreenshotAction = async (
    sessionId: SessionId,
): Promise<string | undefined> => {
    const sourceStore = useSourceStore();
    if (!sourceStore.selectedSourceId) {
        createTextToast(
            'No input source selected.',
            'Please select a valid input source from the options menu.',
            'destructive',
            true,
        );
        return;
    }

    let screenCaptureData: string;
    try {
        ({ screenCaptureData } = await requestScreenshotAsync(
            sourceStore.selectedSourceId,
            sessionId,
            2500,
        ));
    } catch (error) {
        logger.error('Screenshot capture failed:', error);
        createTextToast(
            'Screenshot capture failed',
            'Please verify the selected input source and try again.',
            'destructive',
            true,
        );
        return;
    }

    if (screenCaptureData === 'data:image/png;base64,') {
        createTextToast(
            'Invalid input received',
            'Please select a different input source (Options -> Input source).',
            'destructive',
            true,
        );
        return;
    }

    if (
        sourceStore.selectedSourceName?.toLowerCase().includes('hackerrank') ||
        sourceStore.selectedSourceName?.toLowerCase().includes('hacker rank')
    ) {
        try {
            screenCaptureData = await cropImage(screenCaptureData, 'up', 170);
        } catch (error) {
            logger.error('Image cropping failed:', error);
        }
    }

    try {
        screenCaptureData = await constrainScreenshotSize(screenCaptureData);
    } catch (error) {
        logger.error('Image resizing failed:', error);
        createTextToast(
            'Screenshot is too large',
            'Please select a smaller input source and try again.',
            'destructive',
            true,
        );
        return;
    }

    return screenCaptureData;
};
